/** Live device-status subscription and per-session warning analytics for settings tabs. */
import { useEffect, useMemo } from 'react';
import type { Device, Model } from '@studio/models/device';
import type { Agent } from '@studio/models/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import type { MultiDeviceSelector } from '#~/statequery/types.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { useDepot } from '#~/depot/hooks.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
import {
  hasSubtabAnalyticsShown,
  markSubtabAnalyticsShown
} from '#~/notifications/multi-add-origins.js';
import { deriveNeedAttentionTabs, type NeedAttentionTabs } from './need-attention-tabs.js';

// Copy the warning inputs so in-place proxy updates produce a fresh, comparable snapshot.
const attentionStatusSelector = {
  minInterval: 200,
  transform: ({ status }) =>
    status
      ? {
          video: status.video && {
            authenticationStatus: status.video.authenticationStatus,
            connectionStatus: { status: status.video.connectionStatus?.status }
          },
          movement: status.movement && {
            authenticationStatus: status.movement.authenticationStatus,
            connectionStatus: { status: status.movement.connectionStatus?.status }
          }
        }
      : undefined
} satisfies MultiDeviceSelector<unknown>;

/**
 * Minimal device shape required by the hook. Same `Pick<Device, ...>`
 * shape `useChipState` accepts so call sites can pass the existing
 * studio Device record without extra adapters.
 */
type DeviceLite = {
  id: Device['id'];
  modelId?: T_Maybe<Model['id']>;
  agentStatus?: T_Maybe<Agent['status']>;
};

/** All-false sentinel used when `device` is undefined. */
const ALL_FALSE: NeedAttentionTabs = Object.freeze({
  authentication: false,
  video: false,
  control: false,
  driver: false
});

/**
 * Derive the four per-sub-tab "needs attention" booleans for a device.
 *
 * Reads the agent state-sync proxy (status snapshot) and the depot's
 * dismissal slice; calls the pure `deriveNeedAttentionTabs` to apply
 * the four detection rules + the dismissal mask.
 *
 * Emits `MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN` exactly once per
 * (deviceId, tab) per session on each `false → true` transition. Dedup
 * is depot-backed via `hasSubtabAnalyticsShown` /
 * `markSubtabAnalyticsShown` so the count survives React
 * unmount/remount within the same session.
 *
 * @param device - The studio Device record (or undefined when the
 *   call site has no device yet). All-false returned for undefined.
 * @returns `NeedAttentionTabs` with all four keys always present.
 */
export const useNeedAttentionTabs = (device: DeviceLite | undefined): NeedAttentionTabs => {
  // Subscribe to the per-device dismissal slice via `useDepot` so the
  // hook re-runs after `markTabDismissed` writes. Reading via a
  // selector keeps the subscription scoped to a single record (the
  // zustand store still notifies on any depot mutation; React's
  // referential-equality check short-circuits when the selected slice
  // is unchanged).
  // Per `state-sync-optimistic-mutations` (ai.knowledge/platform/studio.client):
  // depot writes are visible on the next render — no roundtrip needed.
  const dismissed = useDepot((µ) => (device ? µ.dismissedNeedAttentionTabs[device.id] : undefined));

  // Device status can change without changing the depot's agent metadata.
  const template = useTemplate(device?.modelId);
  const statusByDevice = useMultiDeviceStateQuery(attentionStatusSelector);
  const status = device ? statusByDevice[device.id] : undefined;

  // The helper reads only the status fields projected by the selector.
  const tabs = useMemo<NeedAttentionTabs>(() => {
    if (!device) return ALL_FALSE;
    return deriveNeedAttentionTabs(
      status as unknown as Parameters<typeof deriveNeedAttentionTabs>[0],
      { agentStatus: device.agentStatus, modelId: device.modelId },
      dismissed ?? {},
      template?.model
    );
    // Memoize over the four primitives the helper actually reads so
    // the returned object has stable identity across renders that
    // didn't change the verdict. `dismissed` is a per-device record
    // (object identity changes only on depot write to this device's
    // slice).
  }, [status, device?.modelId, device?.agentStatus, dismissed, device, template?.model]);

  // Fire analytics on each false→true transition, deduped per
  // (deviceId, tab) by the depot slice. The effect re-runs whenever
  // `tabs` reference changes (which happens on every transition);
  // `hasSubtabAnalyticsShown` short-circuits when the event was
  // already emitted in this session.
  //
  // Per `think-in-systems` (ai.knowledge/global): mirrors the
  // fire-once-and-mark-shown pattern in
  // `use-multi-add-bucket-notifications.ts` — the dedup is the
  // durable record, not a tick-local set.
  useEffect(() => {
    if (!device) return;
    const deviceId = device.id;
    // Iterate the four in-scope tabs (monitoring is NOT in this set —
    // see `NeedAttentionTabs` type comment). `Object.keys(tabs)`
    // returns the four boolean keys exactly because the helper
    // guarantees all-four-keys-always (per
    // `multi-writer-registry-completeness`).
    const keys: ReadonlyArray<keyof NeedAttentionTabs> = [
      'authentication',
      'video',
      'control',
      'driver'
    ];
    for (const tab of keys) {
      if (!tabs[tab]) continue;
      // Per `use-canonical-logger-not-console`
      // (ai.knowledge/platform/studio.client): the analytics helper
      // routes through the canonical logger; no raw console calls.
      if (hasSubtabAnalyticsShown(deviceId, tab)) continue;
      emitMultiAddAnalytics(EVENT_NAMES.MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN, {
        deviceId,
        subtab: tab
      });
      markSubtabAnalyticsShown(deviceId, tab);
    }
  }, [tabs, device]);

  return tabs;
};
